import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./scss/FluentImport.scss"; // Use dedicated Fluent styles
import StepActions from "./StepActions";
import ZoobbeSelect from "../Global/ZoobbeSelect";
import { fetchLiteWorkspaces } from "../../redux/Slices/workspaceSlice";
import { useDispatch } from "react-redux";
import { config } from "../../config";

const FluentImport = ({
  goNext,
  goBack,
  step,
  selectedWorkspace,
  setSelectedWorkspace,
  fluentBoardFile,
  setFluentBoardFile,
  includeMembers,
  setIncludeMembers,
  uploadedFileId,
  setUploadedFileId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const { liteWorkspaces, status } = useSelector((state) => state.workspaces);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchLiteWorkspaces());
  }, [dispatch]);

  const workspaceOptions =
    liteWorkspaces?.map((workspace) => ({
      value: workspace._id.toString(),
      label: workspace.name,
    })) || [];

  useEffect(() => {
    if (workspaceOptions.length && !selectedWorkspace) {
      setSelectedWorkspace(workspaceOptions[0]);
    }
  }, [workspaceOptions, selectedWorkspace, setSelectedWorkspace]);

  // Upload file when workspace is selected and file is already chosen
  useEffect(() => {
    if (selectedWorkspace && fluentBoardFile && !uploadedFileId && !isUploading) {
      uploadFile(fluentBoardFile);
    }
  }, []);

  const uploadFile = async (file) => {
    if (!selectedWorkspace) {
      alert('Please select a workspace first');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams({
        workspaceId: selectedWorkspace.value,
        uploadOnly: 'true', // Flag to indicate upload only, not import
      });

      // Simulate progress for better UX since fetch doesn't support upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 100);

      const response = await fetch(`${config.API_URI}/v1/fluent/upload?${params.toString()}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();
      setUploadedFileId(result.fileId);
      setUploadProgress(100);
      setIsUploading(false);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/json') {
        alert('Please select a valid JSON file');
        return;
      }

      setFluentBoardFile(file);
      setUploadedFileId(null);
      setUploadError(null);

      // Preview file content
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          setFilePreview(jsonData);

          // Upload file immediately after successful preview
          if (selectedWorkspace) {
            uploadFile(file);
          }
        } catch (error) {
          alert('Invalid JSON file format');
          setFluentBoardFile(null);
          setFilePreview(null);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleWorkspaceSelect = (workspace) => {
    setSelectedWorkspace(workspace);
  };

  const handleNext = () => {
    if (!fluentBoardFile) {
      alert('Please select a JSON file');
      return;
    }
    if (!selectedWorkspace) {
      alert('Please select a workspace');
      return;
    }
    if (!uploadedFileId) {
      alert('Please wait for file upload to complete');
      return;
    }
    goNext();
  };

  if (isLoading || status === 'loading') {
    return (
      <div className="fluent-import">
        <div className="container">
          <div className="import-container">
            <div className="loading-spinner">Loading workspaces...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fluent-import">
      <h3>STEP {step} OF 3</h3>
      <h2>Upload Fluent Board JSON File</h2>

      <div className="container">
        <div className="import-container">
          {/* File Upload Section */}
          <div className="file-upload-section">
            <h4>Select JSON File:</h4>
            <div className="file-upload-area">
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="file-input"
                id="fluent-file-input"
              />
              <label htmlFor="fluent-file-input" className="file-upload-label">
                <div className="upload-icon">📁</div>
                <div className="upload-text">
                  {fluentBoardFile ? fluentBoardFile.name : 'Choose Fluent Board JSON file'}
                </div>
              </label>
            </div>

            {/* Upload Progress */}
            {(isUploading || uploadedFileId || uploadError) && (
              <div className="upload-status">
                {isUploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">Uploading... {Math.round(uploadProgress)}%</span>
                  </div>
                )}

                {uploadedFileId && !isUploading && (
                  <div className="upload-success">
                    <span className="success-icon">✅</span>
                    <span>File uploaded successfully</span>
                  </div>
                )}

                {uploadError && (
                  <div className="upload-error">
                    <span className="error-icon">❌</span>
                    <span>Upload failed: {uploadError}</span>
                    <button
                      onClick={() => uploadFile(fluentBoardFile)}
                      className="retry-button"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* File Preview */}
          {filePreview && (
            <div className="file-preview">
              <h4>Board Preview:</h4>
              <div className="preview-info">
                <p><strong>Board:</strong> {filePreview.board?.title || 'Untitled Board'}</p>
                <p><strong>Lists:</strong> {filePreview.board?.stages?.length || 0}</p>
                <p><strong>Cards:</strong> {filePreview.board?.tasks?.filter(task => task.stage_id != null).length || 0}</p>

                <p><strong>Labels:</strong> {filePreview.board?.labels?.length || 0}</p>
                <p><strong>Members:</strong> {filePreview.board?.users?.length || 0}</p>
              </div>

              {/* Show board members */}
              {filePreview.board?.users && filePreview.board.users.length > 0 && (
                <div className="members-preview">
                  <h5>Board Members ({filePreview.board.users.length}):</h5>
                  <div className="members-list">
                    {filePreview.board.users.map((user, index) => (
                      <div key={index} className="member-item">
                        <span className="member-name">{user.display_name}</span>
                        <span className="member-email">{user.user_email}</span>
                      </div>
                    ))}
                  </div>

                  {/* Member Import Option */}
                  <div className="member-import-option">
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={includeMembers}
                        onChange={(e) => setIncludeMembers(e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">Import and assign board members</span>
                    </label>
                    <p className="members-note">
                      {includeMembers
                        ? "Members will be imported and assigned to the board. Existing users will be added directly, new users will be created as invited members."
                        : "Members will not be imported. You can manually add members after import."
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Workspace Selection */}
          <div className="workspace-selection">
            <h4>Select Workspace:</h4>
            <ZoobbeSelect
              options={workspaceOptions}
              value={selectedWorkspace}
              onSelect={handleWorkspaceSelect}
              placeholder="Choose a workspace..."
            />
          </div>

          <StepActions
            onBack={goBack}
            onNext={handleNext}
            isNextDisabled={!fluentBoardFile || !selectedWorkspace || !uploadedFileId || isUploading}
            nextLabel="Next"
          />
        </div>
      </div>
    </div>
  );
};

export default FluentImport;